import React, { useEffect, useState } from "react";
import { Loader2, Plus, Star, Trash2, Eye, EyeOff, Pencil, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORIES = ["Career Support", "Workshops", "Tutoring", "General"];
const BLANK = { quote: "", name: "", role: "", category: "Career Support", rating: 5, service: "", visible: true };

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="testimonial-star-picker">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" onClick={() => onChange(i)} aria-label={`${i} stars`} data-testid={`testimonial-star-${i}`}>
        <Star className={`w-6 h-6 transition-colors ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
      </button>
    ))}
    <span className="text-sm text-slate-500 ml-2">{value}/5</span>
  </div>
);

function Form({ initial, onCancel, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const editing = Boolean(initial.id);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.quote.trim() || !f.name.trim()) return toast.error("Quote and name are required");
    setSaving(true);
    try {
      const payload = { quote: f.quote.trim(), name: f.name.trim(), role: f.role.trim(), category: f.category, rating: Number(f.rating), service: f.service.trim(), visible: f.visible !== false };
      if (editing) await api.put(`/admin/testimonials/${initial.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success(editing ? "Testimonial updated" : "Testimonial published");
      onSaved();
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-teal-200 bg-teal-50/40 p-5" data-testid="testimonial-form">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Quote</Label>
          <Textarea value={f.quote} onChange={set("quote")} rows={5} placeholder="Paste the testimonial exactly as written — quote marks are added automatically." data-testid="testimonial-quote-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input value={f.name} onChange={set("name")} placeholder="Zoe Zhao" data-testid="testimonial-name-input" /></div>
          <div><Label>Role</Label><Input value={f.role} onChange={set("role")} placeholder="First-Year Teacher" data-testid="testimonial-role-input" /></div>
          <div>
            <Label>Category</Label>
            <select value={f.category} onChange={set("category")} className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="testimonial-category-input">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><Label>Service used</Label><Input value={f.service} onChange={set("service")} placeholder="Key Selection Criteria + Mock Interview" data-testid="testimonial-service-input" /></div>
        </div>
        <div><Label className="block mb-2">Rating</Label><StarPicker value={Number(f.rating)} onChange={(v) => setF((p) => ({ ...p, rating: v }))} /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={f.visible !== false} onChange={(e) => setF((p) => ({ ...p, visible: e.target.checked }))} data-testid="testimonial-visible-input" />
          Show on the website
        </label>
        <div className="flex gap-2">
          <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="testimonial-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {editing ? "Save changes" : "Publish testimonial"}
          </Button>
          <Button type="button" variant="outline" className="rounded-full" onClick={onCancel} data-testid="testimonial-cancel-btn"><X className="w-4 h-4" /> Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

export default function TestimonialsPanel() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (t, patch) => {
    const { id, order, created_at, _id, ...rest } = t;
    await api.put(`/admin/testimonials/${t.id}`, { ...rest, ...patch });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the testimonial from ${t.name}?`)) return;
    await api.delete(`/admin/testimonials/${t.id}`);
    toast.success("Testimonial deleted");
    load();
  };

  if (!items) return <Loader2 className="w-5 h-5 animate-spin text-teal-600" data-testid="testimonials-panel-loading" />;

  return (
    <div className="space-y-4" data-testid="testimonials-panel">
      {editing ? (
        <Form initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      ) : (
        <Button onClick={() => setEditing(BLANK)} className="bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="testimonial-add-btn">
          <Plus className="w-4 h-4" /> Add testimonial
        </Button>
      )}

      <div className="space-y-3">
        {items.map((t) => (
          <Card key={t.id} className="border-slate-200 p-5" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</span>
                  <Badge variant="secondary" className="text-[10px]">{t.category}</Badge>
                  {t.visible === false && <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-[10px]">Hidden</Badge>}
                </div>
                <p className="text-sm text-slate-700 mt-2 line-clamp-3">“{t.quote}”</p>
                <p className="text-sm font-semibold text-slate-900 mt-2">{t.name} <span className="font-normal text-slate-500">· {t.role}</span></p>
                {t.service && <p className="text-xs text-teal-700 mt-1">{t.service}</p>}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(t)} data-testid={`testimonial-edit-${t.id}`}><Pencil className="w-4 h-4" /> Edit</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => save(t, { visible: t.visible === false })} data-testid={`testimonial-toggle-${t.id}`}>
                  {t.visible === false ? <><Eye className="w-4 h-4" /> Show</> : <><EyeOff className="w-4 h-4" /> Hide</>}
                </Button>
                <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:text-red-700" onClick={() => remove(t)} data-testid={`testimonial-delete-${t.id}`}><Trash2 className="w-4 h-4" /> Delete</Button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-500" data-testid="testimonials-panel-empty">No testimonials yet — add your first above.</p>}
      </div>
    </div>
  );
}
